import { Injectable } from "@angular/core";
import { concatMap, mergeMap, Observable, of, tap, toArray } from "rxjs";
import { toNewReportDto } from "../sync-mapper";
import { OnPremiseSyncDataState } from "./on-premise-sync-state.service";
import { InspectionDbService } from "../../database/inspection-db.service";
import { InspectionSchema } from "../../../_offline/models/inspection.schema";
import { IndividualReportDbService } from "../../database/individual-report-db.service";
import { IndividualReportSchema } from "../../../_offline/models/individual-report.schema";
import { InspectionReportService } from "../../http-api/inspection-report.service";
import { InspectionReport, InspectionReportUpdateTexts, PdfOptions } from "../../../_objects/inspection-report";
import { MeasurementModeName } from "../../../_objects/measurement-mode-name";
import { TranslateService } from "@ngx-translate/core";
import { INSPECTION_STATUS_CODE } from "../../../app.constants";

@Injectable({
  providedIn: "root"
})
export class InspectionReportSyncService {
  constructor(
    private inspectionDbService: InspectionDbService,
    private reportDbService: IndividualReportDbService,
    private reportService: InspectionReportService,
    private syncState: OnPremiseSyncDataState,
    private translate: TranslateService
  ) {}

  createReports(): Observable<any[]> {
    return this.inspectionDbService.getAllOnPremise().pipe(
      concatMap((inspections) => inspections),
      mergeMap((inspection) => this.createReportFromInspection(inspection)),
      toArray()
    );
  }

  private createReportFromInspection(inspection: InspectionSchema): Observable<InspectionReport> {
    return this.reportDbService.getAllOnPremise(inspection.id).pipe(
      concatMap((reportSchemas) => reportSchemas),
      mergeMap((reportSchema) =>
        this.postReport(reportSchema).pipe(
          mergeMap((report) => this.postEditableTexts(report, reportSchema)),
          mergeMap((report) => this.createReportPdf(report, reportSchema))
        )
      )
    );
  }

  private postReport(report: IndividualReportSchema) {
    const createdInspectionId = this.syncState.getInspectionId(report.inspectionId);
    const createdReportTemplateId = this.syncState.getReportTemplateId(report.templateId);
    const updatedReportLocations = report.reportLocations
      ? JSON.parse(report.reportLocations).map((location) => {
          const updatedImageIds = location.imageIds.map((imageId) => this.syncState.getInspectionImageId(imageId));
          const dedupUpdatedImageIds = Array.from(new Set(updatedImageIds));
          return { ...location, imageIds: dedupUpdatedImageIds };
        })
      : [];
    const reportNew = {
      ...toNewReportDto(report, createdInspectionId, createdReportTemplateId),
      reportLocations: updatedReportLocations
    };
    return this.reportService
      .createInspectionReport(reportNew)
      .pipe(tap((createdReport) => this.syncState.updateReport(report.id, createdReport.id)));
  }

  private postEditableTexts(report: InspectionReport, reportSchema: IndividualReportSchema): Observable<InspectionReport> {
    const inspectionReportUpdateTexts: InspectionReportUpdateTexts = {
      editableTexts: JSON.parse(reportSchema.editableTexts),
      reportId: report.reportId,
      excludedInputGroups: []
    };
    return this.reportService
      .patchInspectionReportTexts(inspectionReportUpdateTexts)
      .pipe(tap((createdReport) => this.syncState.updateReport(report.id, createdReport.id)));
  }

  private createReportPdf(report: InspectionReport, reportSchema: IndividualReportSchema): Observable<InspectionReport> {
    // We only create pdf for completed reports
    if (reportSchema.status !== INSPECTION_STATUS_CODE.COMPLETED) {
      return of();
    }
    const pdfOptions = this.reportService.getPdfOptions(Number(report.finalQuality), false);
    const measurementMode = new MeasurementModeName(this.translate);
    return this.reportService.createReportPdfAsync(report.reportId, pdfOptions, measurementMode);
  }
}
